import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from './pagination.config';

export interface PaginationChange {
  page: number;
  pageSize: number;
}

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent {
  @Input() page = DEFAULT_PAGE;
  @Input() pageSize = DEFAULT_PAGE_SIZE;
  @Input() totalCount = 0;
  @Input() loading = false;
  @Input() itemLabel = 'items';
  @Output() readonly paginationChange = new EventEmitter<PaginationChange>();

  readonly pageSizeOptions: readonly number[] = PAGE_SIZE_OPTIONS;

  get first(): number {
    return (this.page - 1) * this.pageSize;
  }

  get rangeStart(): number {
    return this.totalCount === 0 ? 0 : this.first + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.first + this.pageSize, this.totalCount);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  get visiblePages(): number[] {
    const visibleCount = Math.min(5, this.totalPages);
    let start = Math.max(1, this.page - Math.floor(visibleCount / 2));
    const end = Math.min(this.totalPages, start + visibleCount - 1);
    start = Math.max(1, end - visibleCount + 1);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  goToPage(page: number): void {
    if (this.loading || page === this.page || page < 1 || page > this.totalPages) {
      return;
    }

    this.paginationChange.emit({ page, pageSize: this.pageSize });
  }

  changePageSize(value: string): void {
    if (this.loading) {
      return;
    }

    const pageSize = Number(value);
    if (!this.pageSizeOptions.includes(pageSize) || pageSize === this.pageSize) {
      return;
    }

    this.paginationChange.emit({ page: DEFAULT_PAGE, pageSize });
  }
}
